import React, { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { KatexPreviewPanel } from '../ui/KatexPreview';
import ImageUpload from '../ui/ImageUpload';
import { QUESTION_TYPES, DIFFICULTY_LEVELS, EXAM_TYPES, SUBJECTS, CLASS_LEVELS, getTopicsForSubject } from '../../constants/questionConstants';

const AddQuestionModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    text: '',
    questionImageUrl: '',
    options: [
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' }
    ],
    subject: '',
    topic: '',
    subtopic: '',
    category: '',
    difficulty: 'MEDIUM',
    accessScope: 'GLOBAL',
    classLevel: '',
    passage: '',
    passageId: '',
    // kitId removed - questions are now categorized by tags only
    tenantId: '',
    tags: [], // Primary categorization method
    examTypes: [],
    explanation: '',
    explanationImageUrl: '',
    reference: '',
    questionType: 'MCQ_SINGLE_CORRECT',
    // New fields for different question types
    numericalAnswer: '', // For FILL_IN_THE_BLANK_NUMERIC
    essayPrompt: '', // For ESSAY questions
    essayMaxWords: 500, // Maximum word count for essays
    matchingPairs: [ // For MATCHING questions
      { left: '', right: '', id: 1 },
      { left: '', right: '', id: 2 }
    ]
  });

  const [tagInput, setTagInput] = useState('');
  const [examTypeInput, setExamTypeInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showImageUploads, setShowImageUploads] = useState(false);

  // Real data from API
  const [tenants, setTenants] = useState([]);
  const [kits, setKits] = useState([]);
  const [kitInput, setKitInput] = useState(''); // For manual kit entry

  // Dynamic data from API
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [subtopicOptions, setSubtopicOptions] = useState([]);
  const [difficultyOptions, setDifficultyOptions] = useState([]);
  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);

  const [examTypeOptions, setExamTypeOptions] = useState([]);

  const resetForm = () => {
    setFormData({
      text: '',
      questionImageUrl: '',
      options: [
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' },
        { text: '', isCorrect: false, imageUrl: '' }
      ],
      subject: '',
      topic: '',
      subtopic: '',
      category: '',
      difficulty: 'Medium',
      accessScope: 'GLOBAL',
      classLevel: '',
      passage: '',
      passageId: '',
      // kitId removed - questions are now categorized by tags only
      tenantId: '',
      tags: [], // Primary categorization method
      examTypes: [],
      explanation: '',
      explanationImageUrl: '',
      reference: '',
      questionType: 'MCQ_SINGLE_CORRECT',
      // New fields for different question types
      numericalAnswer: '',
      essayPrompt: '',
      essayMaxWords: 500,
      matchingPairs: [
        { left: '', right: '', id: 1 },
        { left: '', right: '', id: 2 }
      ]
    });
    setTagInput('');
    setExamTypeInput('');
  };

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      resetForm();
    }
  }, [isOpen]);

  // Auto-enable image uploads when images are added to the form
  useEffect(() => {
    const hasImages = !!(
      formData.questionImageUrl ||
      formData.explanationImageUrl ||
      (formData.options && formData.options.some(opt => opt.imageUrl))
    );

    // Only auto-enable, don't auto-disable to preserve user choice
    if (hasImages && !showImageUploads) {
      setShowImageUploads(true);
    }
  }, [formData.questionImageUrl, formData.explanationImageUrl, formData.options, showImageUploads]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch only dynamic data that can't be predefined
      const [
        tenantsResponse,
        kitsResponse
      ] = await Promise.all([
        superAdminAPI.getAllTenants({ size: 100 }), // Get first 100 tenants
        superAdminAPI.getActiveKits()
      ]);

      // Use predefined constants for dropdown options
      setSubjectOptions(SUBJECTS);
      setDifficultyOptions(DIFFICULTY_LEVELS);
      setQuestionTypeOptions(QUESTION_TYPES);
      setExamTypeOptions(EXAM_TYPES);

      if (tenantsResponse.data?.content) {
        setTenants(tenantsResponse.data.content);
      }

      if (kitsResponse.data) {
        setKits(kitsResponse.data);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      // Set fallback data on error so form still works
      setTenants([]);
      setKits([]);
      // Use predefined constants even on error
      setSubjectOptions(SUBJECTS);
      setDifficultyOptions(DIFFICULTY_LEVELS);
      setQuestionTypeOptions(QUESTION_TYPES);
      setExamTypeOptions(EXAM_TYPES);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'accessScope') {
      setFormData(prev => ({
        ...prev,
        accessScope: value,
        tenantId: value === 'GLOBAL' ? '' : prev.tenantId
      }));
      return;
    }

    if (field === 'tenantId') {
      setFormData(prev => ({
        ...prev,
        tenantId: value,
        accessScope: value ? 'TENANT' : 'GLOBAL'
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Load topics when subject changes
    if (field === 'subject' && value) {
      loadTopics(value);
      // Clear topic and subtopic when subject changes
      setFormData(prev => ({
        ...prev,
        topic: '',
        subtopic: ''
      }));
      setSubtopicOptions([]);
    }

    // Load subtopics when topic changes
    if (field === 'topic' && value) {
      loadSubtopics(value);
      // Clear subtopic when topic changes
      setFormData(prev => ({
        ...prev,
        subtopic: ''
      }));
    }
  };

  const loadTopics = (subject) => {
    // Use predefined constants instead of API call
    const topics = getTopicsForSubject(subject);
    setTopicOptions(topics);
  };

  const loadSubtopics = async (topic) => {
    try {
      const response = await superAdminAPI.getQuestionSubtopics(formData.subject, topic);
      if (response.data) {
        setSubtopicOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading subtopics:', error);
      setSubtopicOptions([]);
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index][field] = value;
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  // Enhanced handlers for automatic text hints when images are uploaded
  const handleQuestionImageChange = (imageUrl) => {
    handleInputChange('questionImageUrl', imageUrl || '');

    // Add automatic text hint when image is uploaded
    if (imageUrl && !formData.text.includes('[See Image]')) {
      const currentText = formData.text.trim();
      const newText = currentText ? `${currentText} [See Image]` : '[See Image]';
      handleInputChange('text', newText);
    }
  };

  const handleOptionImageChange = (index, imageUrl) => {
    handleOptionChange(index, 'imageUrl', imageUrl || '');

    // Add automatic text hint when option image is uploaded
    if (imageUrl && !formData.options[index].text.includes('[See Image]')) {
      const currentText = formData.options[index].text.trim();
      const newText = currentText ? `${currentText} [See Image]` : '[See Image]';
      handleOptionChange(index, 'text', newText);
    }
  };

  const handleExplanationImageChange = (imageUrl) => {
    handleInputChange('explanationImageUrl', imageUrl || '');

    // Add automatic text hint when explanation image is uploaded
    if (imageUrl && !formData.explanation.includes('[See Image]')) {
      const currentText = formData.explanation.trim();
      const newText = currentText ? `${currentText} [See Image]` : '[See Image]';
      handleInputChange('explanation', newText);
    }
  };

  const handleCorrectOptionChange = (index) => {
    const newOptions = formData.options.map((option, i) => ({
      ...option,
      isCorrect: i === index
    }));
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addExamType = () => {
    if (examTypeInput.trim() && !formData.examTypes.includes(examTypeInput.trim())) {
      setFormData(prev => ({
        ...prev,
        examTypes: [...prev.examTypes, examTypeInput.trim()]
      }));
      setExamTypeInput('');
    }
  };

  const removeExamType = (typeToRemove) => {
    setFormData(prev => ({
      ...prev,
      examTypes: prev.examTypes.filter(type => type !== typeToRemove)
    }));
  };

  // Helper functions for matching pairs
  const addMatchingPair = () => {
    const newId = Math.max(...formData.matchingPairs.map(p => p.id)) + 1;
    setFormData(prev => ({
      ...prev,
      matchingPairs: [...prev.matchingPairs, { left: '', right: '', id: newId }]
    }));
  };

  const removeMatchingPair = (id) => {
    if (formData.matchingPairs.length > 2) { // Keep minimum 2 pairs
      setFormData(prev => ({
        ...prev,
        matchingPairs: prev.matchingPairs.filter(pair => pair.id !== id)
      }));
    }
  };

  const updateMatchingPair = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      matchingPairs: prev.matchingPairs.map(pair =>
        pair.id === id ? { ...pair, [field]: value } : pair
      )
    }));
  };

  // Handle question type change
  const handleQuestionTypeChange = (type) => {
    setFormData(prev => {
      const newData = { ...prev, questionType: type };

      if (type === 'TRUE_FALSE') {
        // For True/False, set exactly 2 options
        newData.options = [
          { text: 'True', isCorrect: false, imageUrl: '' },
          { text: 'False', isCorrect: false, imageUrl: '' }
        ];
      } else if (type === 'FILL_IN_THE_BLANK_NUMERIC') {
        // For numeric questions, clear options
        newData.options = [];
        newData.numericalAnswer = '';
      } else if (type === 'ESSAY') {
        // For essay questions, clear options
        newData.options = [];
        newData.essayPrompt = '';
        newData.essayMaxWords = 500;
      } else if (type === 'MATCHING') {
        // For matching questions, clear options
        newData.options = [];
        newData.matchingPairs = [
          { left: '', right: '', id: 1 },
          { left: '', right: '', id: 2 }
        ];
      } else {
        // For MCQ types, ensure we have 4 options
        newData.options = [
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' }
        ];
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.text.trim()) {
      alert('Question text is required');
      return;
    }

    // Question type specific validation
    if (formData.questionType === 'MCQ_SINGLE_CORRECT' ||
        formData.questionType === 'MCQ_MULTIPLE_CORRECT' ||
        formData.questionType === 'TRUE_FALSE' ||
        formData.questionType === 'MULTIPLE_CHOICE') {
      if (formData.options.some(option => !option.text.trim())) {
        alert('All options must be filled');
        return;
      }
      if (!formData.options.some(option => option.isCorrect)) {
        alert('Please select the correct answer');
        return;
      }
    } else if (formData.questionType === 'FILL_IN_THE_BLANK_NUMERIC') {
      if (!formData.numericalAnswer.trim()) {
        alert('Numerical answer is required');
        return;
      }
    } else if (formData.questionType === 'ESSAY') {
      if (!formData.essayPrompt.trim()) {
        alert('Essay prompt is required');
        return;
      }
      if (formData.essayMaxWords <= 0) {
        alert('Maximum word count must be greater than 0');
        return;
      }
    } else if (formData.questionType === 'MATCHING') {
      if (formData.matchingPairs.some(pair => !pair.left.trim() || !pair.right.trim())) {
        alert('All matching pairs must be filled');
        return;
      }
      if (formData.matchingPairs.length < 2) {
        alert('At least 2 matching pairs are required');
        return;
      }
    }

    if (!formData.subject) {
      alert('Subject is required');
      return;
    }

    if (!formData.topic) {
      alert('Topic is required');
      return;
    }

    setIsSubmitting(true);
    try {
      // Derive correct answer based on question type
      let correctAnswer = '';
      let options = [];

      if (formData.questionType === 'MCQ_SINGLE_CORRECT' ||
          formData.questionType === 'MCQ_MULTIPLE_CORRECT' ||
          formData.questionType === 'TRUE_FALSE' ||
          formData.questionType === 'MULTIPLE_CHOICE') {
        const correctOptions = formData.options
          .map((option, index) => option.isCorrect ? (index + 1).toString() : null)
          .filter(Boolean);
        correctAnswer = correctOptions.join(',');
        options = formData.options.map((opt, index) => ({
          text: opt.text,
          isCorrect: opt.isCorrect,
          imageUrl: opt.imageUrl || null,
          optionLetter: (index + 1).toString(),
          optionIndex: index + 1
        }));
      } else if (formData.questionType === 'FILL_IN_THE_BLANK_NUMERIC') {
        correctAnswer = formData.numericalAnswer;
        options = [];
      } else if (formData.questionType === 'ESSAY') {
        correctAnswer = ''; // Essays don't have predefined correct answers
        options = [];
      } else if (formData.questionType === 'MATCHING') {
        correctAnswer = formData.matchingPairs.map(pair => `${pair.left}:${pair.right}`).join(',');
        options = [];
      }

      const selectedTenant = tenants.find(tenant => tenant.id === formData.tenantId);

      // Transform form data to match API expectations
      const submitData = {
        questionText: formData.text,
        questionImageUrl: formData.questionImageUrl || null,
        type: formData.questionType,
        options: options,
        correctAnswers: formData.questionType === 'FILL_IN_THE_BLANK_NUMERIC'
          ? [formData.numericalAnswer]
          : formData.questionType === 'ESSAY'
          ? []
          : formData.questionType === 'MATCHING'
          ? formData.matchingPairs.map(pair => `${pair.left}:${pair.right}`)
          : formData.options.filter(opt => opt.isCorrect).map(opt => opt.text),
        // Additional fields for specific question types
        essayPrompt: formData.questionType === 'ESSAY' ? formData.essayPrompt : null,
        essayMaxWords: formData.questionType === 'ESSAY' ? formData.essayMaxWords : null,
        matchingPairs: formData.questionType === 'MATCHING' ? formData.matchingPairs : null,
        explanation: formData.explanation || null,
        explanationImageUrl: formData.explanationImageUrl || null,
        reference: formData.reference || null,
        classLevel: formData.classLevel || null,
        passage: formData.passage || null,
        passageId: formData.passageId || null,
        subject: formData.subject,
        topic: formData.topic,
        subtopic: formData.subtopic || null,
        difficultyLevel: formData.difficulty,
        examTypes: formData.examTypes,
        tags: formData.tags,
        accessScope: formData.accessScope,
        tenantId: formData.accessScope === 'TENANT' ? (formData.tenantId || null) : null,
        tenantCodes: formData.accessScope === 'TENANT' && selectedTenant
          ? [selectedTenant.code || selectedTenant.name || selectedTenant.id]
          : [],
        category: formData.subject, // For API compatibility
        correctAnswer: correctAnswer // Legacy field
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error creating question:', error);
      alert('Failed to create question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-7xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Add New Question</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors duration-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Form Section */}
          <div className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question Type and Difficulty - Moved to top for immediate form changes */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Question Type *
                </label>
                <select
                  value={formData.questionType}
                  onChange={(e) => handleQuestionTypeChange(e.target.value)}
                  className="input-field border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                >
                  {questionTypeOptions.map(type => (
                    <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Difficulty *
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => handleInputChange('difficulty', e.target.value)}
                  className="input-field border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  {difficultyOptions.map(difficulty => (
                    <option key={difficulty} value={difficulty}>{difficulty}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Select the question type first - this will change the form fields below
            </p>
          </div>

          {/* Passage fields for Reading Comprehension */}
          {formData.questionType === 'READING_COMPREHENSION' && (
            <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  Passage ID *
                </label>
                <input
                  type="text"
                  value={formData.passageId}
                  onChange={(e) => handleInputChange('passageId', e.target.value)}
                  className="input-field"
                  placeholder="Unique ID to group sub-questions (e.g., PASSAGE_001)"
                  required
                />
                <p className="text-xs text-amber-700 mt-1">
                  All sub-questions sharing this ID will be grouped under the same passage
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  Passage Text
                </label>
                <textarea
                  value={formData.passage}
                  onChange={(e) => handleInputChange('passage', e.target.value)}
                  className="input-field h-32 resize-y"
                  placeholder="Enter the passage text (shared across all sub-questions with the same Passage ID)"
                />
                <p className="text-xs text-amber-700 mt-1">
                  The passage only needs to be entered on one sub-question; others with the same Passage ID will share it
                </p>
              </div>
            </div>
          )}

          {/* Subject, Topic, and Subtopic */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="input-field"
                placeholder="Enter or select subject..."
                list="subject-suggestions"
                required
              />
              <datalist id="subject-suggestions">
                {subjectOptions.map(subject => (
                  <option key={subject} value={subject} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Topic *
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => handleInputChange('topic', e.target.value)}
                className="input-field"
                placeholder="Enter or select topic..."
                list="topic-suggestions"
                required
              />
              <datalist id="topic-suggestions">
                {topicOptions.map(topic => (
                  <option key={topic} value={topic} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Subtopic
              </label>
              <input
                type="text"
                value={formData.subtopic}
                onChange={(e) => handleInputChange('subtopic', e.target.value)}
                className="input-field"
                placeholder="Enter or select subtopic..."
                list="subtopic-suggestions"
              />
              <datalist id="subtopic-suggestions">
                {subtopicOptions.map(subtopic => (
                  <option key={subtopic} value={subtopic} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Question Text *
            </label>
            <textarea
              value={formData.text}
              onChange={(e) => handleInputChange('text', e.target.value)}
              className="input-field h-24 resize-none"
              placeholder="Enter the question text..."
              required
            />
          </div>

          {/* Image Upload Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
            <div>
              <label className="text-sm font-medium text-slate-700">
                Enable Image Uploads
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Show image upload options for question, options, and explanation
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showImageUploads}
                onChange={(e) => setShowImageUploads(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Question Image */}
          {showImageUploads && (
            <ImageUpload
              label="Question Image (Optional)"
              value={formData.questionImageUrl}
              onChange={handleQuestionImageChange}
              entityId={`question-${Date.now()}`}
              imageType="QUESTION"
            />
          )}

          {/* Options - Only show for MCQ and TRUE_FALSE */}
          {(formData.questionType === 'MCQ_SINGLE_CORRECT' ||
            formData.questionType === 'MCQ_MULTIPLE_CORRECT' ||
            formData.questionType === 'TRUE_FALSE' ||
            formData.questionType === 'MULTIPLE_CHOICE') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Answer Options *
              </label>
              <div className="space-y-4">
                {formData.options.map((option, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="correctOption"
                          checked={option.isCorrect}
                          onChange={() => handleCorrectOptionChange(index)}
                          className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-slate-300"
                        />
                        <span className="ml-2 text-sm font-medium text-slate-700">
                          {index + 1}
                        </span>
                      </div>
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                        className="input-field flex-1"
                        placeholder={`Option ${index + 1}`}
                        required
                      />
                    </div>

                    {/* Option Image Upload */}
                    {showImageUploads && (
                      <div className="ml-6">
                        <ImageUpload
                          label={`Option ${index + 1} Image (Optional)`}
                          value={option.imageUrl}
                          onChange={(imageUrl) => handleOptionImageChange(index, imageUrl)}
                          entityId={`option-${index}-${Date.now()}`}
                          imageType="OPTION"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Select the radio button next to the correct answer
              </p>
            </div>
          )}

          {/* Numerical Answer - Only for FILL_IN_THE_BLANK_NUMERIC */}
          {formData.questionType === 'FILL_IN_THE_BLANK_NUMERIC' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Numerical Answer *
              </label>
              <input
                type="number"
                value={formData.numericalAnswer}
                onChange={(e) => handleInputChange('numericalAnswer', e.target.value)}
                className="input-field"
                placeholder="Enter the numerical answer"
                required
              />
            </div>
          )}

          {/* Essay Fields - Only for ESSAY */}
          {formData.questionType === 'ESSAY' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Essay Prompt *
                </label>
                <textarea
                  value={formData.essayPrompt}
                  onChange={(e) => handleInputChange('essayPrompt', e.target.value)}
                  className="input-field h-24 resize-none"
                  placeholder="Enter the essay prompt or question..."
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Provide clear instructions for what the student should write about
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Maximum Word Count *
                </label>
                <input
                  type="number"
                  value={formData.essayMaxWords}
                  onChange={(e) => handleInputChange('essayMaxWords', parseInt(e.target.value) || 500)}
                  className="input-field"
                  min="50"
                  max="2000"
                  placeholder="500"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Recommended range: 100-1000 words
                </p>
              </div>
            </div>
          )}

          {/* Matching Pairs - Only for MATCHING */}
          {formData.questionType === 'MATCHING' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-slate-700">
                  Matching Pairs *
                </label>
                <button
                  type="button"
                  onClick={addMatchingPair}
                  className="px-3 py-1 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700"
                >
                  Add Pair
                </button>
              </div>
              <div className="space-y-3">
                {formData.matchingPairs.map((pair) => (
                  <div key={pair.id} className="grid grid-cols-2 gap-4 p-3 border rounded-lg">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Left Side</label>
                      <input
                        type="text"
                        value={pair.left}
                        onChange={(e) => updateMatchingPair(pair.id, 'left', e.target.value)}
                        className="input-field"
                        placeholder="Enter left side text..."
                        required
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-slate-600 mb-1">Right Side</label>
                        <input
                          type="text"
                          value={pair.right}
                          onChange={(e) => updateMatchingPair(pair.id, 'right', e.target.value)}
                          className="input-field"
                          placeholder="Enter right side text..."
                          required
                        />
                      </div>
                      {formData.matchingPairs.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeMatchingPair(pair.id)}
                          className="ml-2 px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Create pairs that students need to match. Minimum 2 pairs required.
              </p>
            </div>
          )}





          {/* Class Level */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Class (Optional)
              </label>
              <select
                value={formData.classLevel}
                onChange={(e) => handleInputChange('classLevel', e.target.value)}
                className="input-field"
              >
                <option value="">Select Class</option>
                {CLASS_LEVELS.map(level => (
                  <option key={level} value={level}>Class {level}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Access and Kit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Access Scope
              </label>
              <select
                value={formData.accessScope}
                onChange={(e) => handleInputChange('accessScope', e.target.value)}
                className="input-field"
              >
                <option value="GLOBAL">Global - available to all admin tenants</option>
                <option value="TENANT">Tenant-specific - restricted to one tenant</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Use global for centrally shared questions. Use tenant-specific when only one tenant should access the question.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Target Tenant
              </label>
              <select
                value={formData.tenantId}
                onChange={(e) => handleInputChange('tenantId', e.target.value)}
                className="input-field"
                disabled={formData.accessScope !== 'TENANT'}
              >
                <option value="">Select tenant</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name} ({tenant.code || tenant.id})</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {formData.accessScope === 'TENANT'
                  ? 'The selected tenant code will be saved with this question and can be changed later.'
                  : 'Not required for global questions.'}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Kit (Optional)
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={kitInput}
                  onChange={(e) => {
                    setKitInput(e.target.value);
                    handleInputChange('kitId', e.target.value);
                  }}
                  className="input-field"
                  placeholder="Enter or search kit name..."
                  list="kit-suggestions"
                />
                <datalist id="kit-suggestions">
                  {kits.map(kit => (
                    <option key={kit.id} value={kit.name} />
                  ))}
                </datalist>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Type to search existing kits or enter a new kit name. Leave empty to auto-generate.
              </p>
            </div>
          </div>



          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tags
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="input-field flex-1"
                placeholder="Add a tag..."
              />
              <button
                type="button"
                onClick={addTag}
                className="btn-secondary"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-1.5 text-slate-500 hover:text-slate-700"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Exam Types */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Exam Types
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={examTypeInput}
                onChange={(e) => setExamTypeInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExamType())}
                className="input-field flex-1"
                placeholder="Add exam type (e.g., JEE Main, NEET)..."
              />
              <button
                type="button"
                onClick={addExamType}
                className="btn-secondary"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.examTypes.map(type => (
                <span
                  key={type}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700"
                >
                  {type}
                  <button
                    type="button"
                    onClick={() => removeExamType(type)}
                    className="ml-1.5 text-slate-500 hover:text-slate-700"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reference (Optional)
            </label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => handleInputChange('reference', e.target.value)}
              className="input-field"
              placeholder="Book, article, chapter reference"
            />
            <p className="text-xs text-slate-500 mt-1">
              Source reference where this question is inspired from
            </p>
          </div>

          {/* Explanation */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Explanation (Optional)
            </label>
            <textarea
              value={formData.explanation}
              onChange={(e) => handleInputChange('explanation', e.target.value)}
              className="input-field h-20 resize-none"
              placeholder="Provide an explanation for the correct answer..."
            />
          </div>

          {/* Explanation Image */}
          {showImageUploads && (
            <ImageUpload
              label="Explanation Image (Optional)"
              value={formData.explanationImageUrl}
              onChange={handleExplanationImageChange}
              entityId={`explanation-${Date.now()}`}
              imageType="EXPLANATION"
            />
          )}

          {/* Submit Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Question'}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Live Preview</h3>
            <KatexPreviewPanel
              questionText={formData.text}
              questionImageUrl={formData.questionImageUrl}
              questionType={formData.questionType}
              options={(formData.questionType === 'FILL_IN_THE_BLANK_NUMERIC' ||
                       formData.questionType === 'ESSAY' ||
                       formData.questionType === 'MATCHING') ? [] : formData.options}
              numericalAnswer={formData.questionType === 'FILL_IN_THE_BLANK_NUMERIC' ? formData.numericalAnswer : undefined}
              essayPrompt={formData.questionType === 'ESSAY' ? formData.essayPrompt : undefined}
              essayMaxWords={formData.questionType === 'ESSAY' ? formData.essayMaxWords : undefined}
              matchingPairs={formData.questionType === 'MATCHING' ? formData.matchingPairs : undefined}
              explanation={formData.explanation}
              explanationImageUrl={formData.explanationImageUrl}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestionModal;
